import type { Feature } from 'geojson'
import type L from 'leaflet'
import { createContext, FC, PropsWithChildren, useContext, useMemo, useState } from 'react'

interface MapContextProps {
  perimeter: Feature
  legendDirection: 'row' | 'column'
  mapWidth: number
  map?: L.Map | null
  setMap?: (map: L.Map | null) => void
  showDeletePolygons?: boolean
  setShowDeletePolygons?: (show: boolean | ((prev: boolean) => boolean)) => void
}

const MapContext = createContext<MapContextProps | undefined>(undefined)

interface MapProviderProps {
  values: Omit<MapContextProps, 'showDeletePolygons' | 'setShowDeletePolygons'>
}

export const MapProvider: FC<PropsWithChildren<MapProviderProps>> = ({ children, values }) => {
  const [showDeletePolygons, setShowDeletePolygons] = useState(false)

  const contextValue = useMemo(
    () => ({ ...values, showDeletePolygons, setShowDeletePolygons }),
    [values, showDeletePolygons, setShowDeletePolygons],
  )

  return <MapContext.Provider value={contextValue}>{children}</MapContext.Provider>
}

export const useMapContext = () => {
  const context = useContext(MapContext)
  if (context === undefined) {
    throw new Error('useMapContext must be used within a MapProvider')
  }
  return context
}
